/* Editor — owns project/files/versions/spec + the mutable working copy
   (in a ref) with a formRev counter, dirty/qa/selPath/running/model/
   telemetry. Ports openProject / refreshProjectMeta / adoptRenderResult
   / apply / discard / generate / promptSend / restore / exportPdf. */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Download, FolderOpen, History, Layers3, MessageSquare, PanelLeft, PanelRight, Save, X } from 'lucide-react';
import {
  architectApi,
  pricingAccess,
  Run,
  Phase,
  Pipeline,
  pipelineHasHistoricalDocument,
  ArchitectProject,
  DocKind,
} from './api';
import { useAuth } from '../auth/AuthContext';
import { proposalAccess } from './proposalApi';
import {
  InlineRename,
  ProjectCode,
  useApprovalStatus,
} from './ProjectsView';
import {
  ProjectRecord,
  FileMeta,
  VersionMeta,
  Spec,
  Qa,
  Telemetry,
  RenderResult,
  ArchitectureEditMode,
  ArchitectureEditRequest,
} from './types';
import {
  MODELS,
  SOURCE_EN,
  ERROR_EN,
  KIND_LABEL,
  deepCopy,
  cleanSpec,
  resolvePath,
  pathKind,
  slugName,
  mapKnownRunMessage,
} from './utils';
import { toast } from './toast';
import SvgCanvas, { SvgCanvasHandle } from './SvgCanvas';
import { InlineTextTarget, replaceInlineText } from './inlineText';
import ContextEditor from './ContextEditor';
import StructureTree, { crumbSegments, KIND_DESC } from './StructureTree';
import { stageAccess } from './stageAccess';
import FilesPanel, { FilesPanelHandle } from './FilesPanel';
import { useWorkspaceDraftGuard } from './useWorkspaceDraftGuard';
import PromptBar from './PromptBar';
import RunProgress, { isOperatorStop } from './RunProgress';
import EditorProjectMenu from './EditorProjectMenu';
import { StageHeader, StageFooter } from './StageChrome';
import ApproveAsSelector, { useApprovalActor } from './ApproveAsSelector';
import { loadArchitectureSnapshotSvg } from './architectureSnapshot';
import { architectureReviewIsStale, architectureReviewVersion, architectureReviewView } from './architectureReview';
import EstimatePanel from './EstimatePanel';
import EstimateApprovalGate, { estimateGateView, useEstimateApprovalGate } from './EstimateApprovalGate';
import { usePipelineResume } from './usePipelineResume';
import ProposalPanel from './ProposalPanel';
import PricingPanel from './PricingPanel';
import LegacyDocuments from './LegacyDocuments';
import SavedPricingHistory from './SavedPricingHistory';
import WorkflowRecoveryDialog from './WorkflowRecoveryDialog';
import CommentsPanel from './CommentsPanel';
import { CommentAnchorProvider, CommentPickingNotice, useCommentWorkspace } from './CommentAnchors';
import { useCommentUpdates } from './useCommentUpdates';
import { useCommentSound } from './useCommentSound';
import { commentsApi, CommentStage, StageComment } from './api';
import { takeLaunchPanel } from './launch';
import './editor-ui.css';
import WorkspaceStageNav, { WorkspaceStage, WorkspaceScene, approvedStageGates } from './WorkspaceStageNav';
import EstimateReferencePane from './EstimateReferencePane';
import './stage-workspace.css';
import { ArchitectureEditDialog, ArchitectureEditModeButton } from './ArchitectureEditMode';

interface Props {
  projectId: string;
  onBack: () => void;
  // Model choice lives in ArchitectPanel: EditorView is keyed by
  // projectId (intentional per-project remount), and the SPA keeps
  // the chosen model sticky across projects.
  model: string;
  setModel: (m: string) => void;
}

// Poll cadence for an in-flight run (ms).
const RUN_POLL_MS = 1500;

/** Human message for a failed run. The backend already stores a
    human-readable message in run.error (the old 502 body text); we only
    map the bare error CODES the ERROR_EN table knows, otherwise surface
    the raw text as-is. */
function runErrorText(raw: string | null): string {
  if (!raw) return 'The generation failed — try again';
  // The run stores "<stage_code>: <human message>" — the code prefix is
  // redundant next to the banner's own "Generation failed:" label.
  const stripped = raw.replace(/^(generate_failed|prompt_failed|render_failed|llm_failed):\s*/, '');
  // Normalise the known sweep/stop free-text (English or a legacy-Russian
  // message from an old pod during a rolling deploy) to canonical English
  // before falling back to the raw text.
  return ERROR_EN[stripped] || ERROR_EN[raw] || mapKnownRunMessage(stripped);
}

function qaText(qa: Qa | null): string | null {
  const q = qa || {};
  const parts: string[] = [];
  if (q.crossings) parts.push(q.crossings + ' line crossings');
  if (q.frame_violations) parts.push(q.frame_violations + ' frame violations');
  if (q.box_hits) parts.push(q.box_hits + ' box overlaps');
  if (!parts.length) return null;
  return (
    '⚠ Standards violated: ' +
    parts.join(', ') +
    '. The poster rendered — double-check the layout.'
  );
}

export default function EditorView({ projectId, onBack, model, setModel }: Props) {
  const [project, setProject] = useState<ProjectRecord | null>(null);
  const [files, setFiles] = useState<FileMeta[]>([]);
  const [versions, setVersions] = useState<VersionMeta[]>([]);
  const [spec, setSpec] = useState<Spec | null>(null);
  // Metadata polls may discover a newer version without replacing this canvas.
  // Only a successful spec + SVG adoption may advance the review identity.
  const [displayedArchitecture, setDisplayedArchitecture] = useState<{ projectId: string; versionId: string | null; specFingerprint?: string | null; approved: boolean } | null>(null);
  const editingApprovedArchitecture = displayedArchitecture?.approved === true || project?.architecture_status === 'approved';
  const [editMode, setEditMode] = useState<ArchitectureEditMode>('wording');
  const [editChoice, setEditChoice] = useState<ArchitectureEditMode>('wording');
  const [editDialog, setEditDialog] = useState<{ purpose: 'mode' | 'structural'; title: string; confirmLabel?: string } | null>(null);
  const [architectureSaveError, setArchitectureSaveError] = useState<string | null>(null);
  const editConfirmation = useRef<((confirmed: boolean) => void) | null>(null);
  useEffect(() => { setEditMode('wording'); setArchitectureSaveError(null); }, [projectId, displayedArchitecture?.versionId]);
  useEffect(() => () => { editConfirmation.current?.(false); editConfirmation.current = null; }, [projectId]);
  const requestStructuralEdit = useCallback((title: string, confirmLabel: string) => new Promise<boolean>(resolve => {
    if (editConfirmation.current) { resolve(false); return; }
    editConfirmation.current = resolve;
    setEditChoice('structural');
    setEditDialog({ purpose: 'structural', title, confirmLabel });
  }), []);
  const closeEditDialog = () => {
    setEditDialog(null);
    editConfirmation.current?.(false); editConfirmation.current = null;
  };
  const acceptEditDialog = () => {
    if (editDialog?.purpose === 'mode') setEditMode(editChoice);
    setEditDialog(null);
    editConfirmation.current?.(true); editConfirmation.current = null;
  };
  const openEditMode = () => { setEditChoice(editMode); setEditDialog({ purpose: 'mode', title: 'Edit architecture' }); };
  const editGuard = useCallback((mode: ArchitectureEditMode): ArchitectureEditRequest | undefined => {
    if (!displayedArchitecture || !displayedArchitecture.specFingerprint) {
      if (editingApprovedArchitecture) throw new Error('Reload the architecture before editing. Your draft is still here.');
      return undefined; // older drafts can predate version/fingerprint metadata
    }
    const baseline = { expected_version_id: displayedArchitecture.versionId, expected_spec_fingerprint: displayedArchitecture.specFingerprint };
    return editingApprovedArchitecture ? { ...baseline, edit_mode: mode } : baseline;
  }, [displayedArchitecture, editingApprovedArchitecture]);
  const [architectureDisplayLoading, setArchitectureDisplayLoading] = useState(false);
  const [qa, setQa] = useState<Qa | null>(null);
  const [selPath, setSelPath] = useState<string | null>(null);
  const [formRev, setFormRev] = useState(0);
  const [dirty, setDirty] = useState(false);
  const editRevision = useRef(0);
  const [running, setRunning] = useState(false);
  // The live async run (generate/prompt) — drives the RunProgress
  // overlay and the busy state. null when idle.
  const [activeRun, setActiveRun] = useState<Run | null>(null);
  // Wall-clock start of the active run (from run.created_at) so a
  // resumed run shows the true elapsed time; null when idle.
  const [runStartMs, setRunStartMs] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [telemetry, setTelemetry] = useState<Telemetry | null>(null);
  // The last failed run's message ("Anthropic: закончились деньги…").
  // A toast disappears in seconds — billing/key problems must stay
  // on screen until dismissed or the next run starts.
  const [runError, setRunError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [activeStage, setActiveStage] = useState<WorkspaceStage>('architecture');
  const [nextStageAfterApproval, setNextStageAfterApproval] = useState<WorkspaceStage | null>(null);
  const [legacyHistoryOpen, setLegacyHistoryOpen] = useState(false);
  const [recoveryProposalId, setRecoveryProposalId] = useState<string | null>(null);
  const stageGateRef = useRef<Record<WorkspaceStage, boolean>>({ architecture: true, estimate: false, pricing: false, proposal: false });
  const stageReasonRef = useRef<Partial<Record<WorkspaceStage, string>>>({});
  const [pricingReferenceOpen, setPricingReferenceOpen] = useState(false);
  const [visitedStages, setVisitedStages] = useState<Partial<Record<WorkspaceStage, boolean>>>({ architecture: true });
  const [documentRevision, setDocumentRevision] = useState(0);
  const [pricingRevision, setPricingRevision] = useState(0);
  const [proposalRevision, setProposalRevision] = useState(0);
  const documentDrafts = useRef<Record<string, { dirty: boolean; busy: boolean }>>({});
  const commentDraftChanged = useCallback((state: { dirty: boolean; busy: boolean }) => { documentDrafts.current.comments = state; }, []);
  const estimateDraftChanged = useCallback((state: { dirty: boolean; busy: boolean }) => { documentDrafts.current.estimate = state; }, []);
  const pricingDraftChanged = useCallback((state: { dirty: boolean; busy: boolean }) => { documentDrafts.current.pricing = state; }, []);
  const proposalDraftChanged = useCallback((state: { dirty: boolean; busy: boolean }) => { documentDrafts.current.proposal = state; }, []);
  const selectStage = useCallback((stage: WorkspaceStage) => {
    if (!stageGateRef.current[stage]) {
      toast(stageReasonRef.current[stage] || 'Approve the previous stage first.', 'info');
      return;
    }
    if (documentDrafts.current.comments?.busy) { toast('Finish sending or stop the voice recording before switching stages.', 'info'); return; }
    setVisitedStages((visited) => ({ ...visited, [stage]: true }));
    setActiveStage(stage);
    setLeftOpen(false);
    setRightOpen(false);
    setPeek(null);
  }, []);
  const requestLeave = useWorkspaceDraftGuard(() => {
    const drafts = Object.values(documentDrafts.current);
    const sources = filesPanelRef.current?.getDraftState?.();
    return {
      dirty: dirty || !!sources?.dirty || drafts.some((state) => state.dirty),
      busy: applying || !!sources?.busy || drafts.some((state) => state.busy),
    };
  }, { onBusy: () => toast('Finish the current save, upload or voice recording before leaving.', 'info') });
  const leaveWorkspace = () => { requestLeave(onBack); };
  // DESIGN43 — stage comments. Counts feed the header badge; the stages the
  // viewer may open come back with them (server-derived rights).
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [commentCounts, setCommentCounts] = useState<Partial<Record<CommentStage, number>>>({});
  // DESIGN47 — «Open in the web app» from the mini app lands straight in the
  // requested panel (once; a panel the viewer may not open simply stays
  // unmounted, exactly as a click would).
  const [pendingLaunch, setPendingLaunch] = useState<ReturnType<typeof takeLaunchPanel>>(null);
  useEffect(() => {
    const launch = takeLaunchPanel(projectId);
    // Effect replay must not clear the one-shot launch before the pipeline loads.
    if (launch) setPendingLaunch(launch);
  }, [projectId]);
  // The reveal target for the Structure tree (DESIGN9 §1): the last
  // selected path, kept alive after the form closes so returning to the
  // tree scrolls that row into view + pulses it.
  const [revealPath, setRevealPath] = useState<string | null>(null);
  // Structure-search query — lifted here so it survives the tree's
  // remounts (the tree unmounts whenever an element is selected).
  const [treeQuery, setTreeQuery] = useState('');
  // Bumped on every CANVAS click so the edit-form container pulses once.
  const [panelPulse, setPanelPulse] = useState(0);
  // Desktop-only sources-panel collapse, persisted to localStorage.
  const [sourcesCollapsed, setSourcesCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('arch.sourcesCollapsed') === '1';
    } catch {
      return false;
    }
  });
  /* DESIGN39 — the structure/editor panel folds the same way, and a collapsed
     panel is one hover away: pointing at its rail slides it out OVER the
     canvas (no reflow, so the poster never jumps), and clicking the rail gives
     that side the room — the opposite panel folds to its own rail. The head
     chevron of a peeked panel pins it open WITHOUT folding the other, so both
     can still be open on a wide screen. */
  const [structureCollapsed, setStructureCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem('arch.structureCollapsed') === '1';
    } catch {
      return false;
    }
  });
  const [peek, setPeek] = useState<'left' | 'right' | null>(null);
  const peekTimer = useRef<number | null>(null);

  const { user, permissions } = useAuth();
  const proposalRights = proposalAccess(user, permissions);
  const architectureRights = stageAccess(user, permissions, 'architecture');
  const estimateRights = stageAccess(user, permissions, 'estimate');
  const access = { ...proposalRights, canGenerate: architectureRights.edit };
  const architectureRightsRef = useRef(architectureRights); architectureRightsRef.current = architectureRights;
  const canProposal = proposalRights.canSee;
  const canPricing = pricingAccess(user, permissions);
  const commentStages = useMemo<CommentStage[]>(() => {
    const out: CommentStage[] = [];
    if (architectureRights.view && (architectureRights.configured || architectureRights.edit)) out.push('architecture');
    if (estimateRights.view) out.push('estimate');
    if (canPricing) out.push('pricing');
    if (canProposal) out.push('proposal');
    return out;
  }, [architectureRights.view, architectureRights.configured, architectureRights.edit, estimateRights.view, canPricing, canProposal]);
  const commentRights = stageAccess(user, permissions, activeStage);
  const canWriteComments = !commentRights.configured || commentRights.edit || commentRights.approve;
  const commentsVisible = commentsOpen && commentStages.includes(activeStage);
  const openComments = useCallback(() => setCommentsOpen(true), []);
  const commentWorkspace = useCommentWorkspace(activeStage, commentStages.includes(activeStage), openComments, canWriteComments);
  const commentWorkspaceRef = useRef(commentWorkspace);
  commentWorkspaceRef.current = commentWorkspace;
  const closedCommentSound = useCommentSound();
  const closedCommentSoundRef = useRef(closedCommentSound.play); closedCommentSoundRef.current = closedCommentSound.play;
  const commentsOpenRef = useRef(commentsVisible); commentsOpenRef.current = commentsVisible;
  const commentKey = `${projectId}:${activeStage}`;
  const commentKeyRef = useRef(commentKey); commentKeyRef.current = commentKey;
  const seenCommentIds = useRef(new Map<string, Set<string>>());
  const closedLoad = useRef<string | null>(null);
  const anchorIndexRequest = useRef(0);
  const refreshAnchorIndex = useCallback(async () => {
    const key = `${projectId}:${activeStage}`;
    const request = ++anchorIndexRequest.current;
    const anchors: StageComment[] = [];
    const cursors = new Set<string>();
    let cursor: string | undefined;
    try {
      do {
        const page = await commentsApi.anchors(projectId, activeStage, cursor);
        if (request !== anchorIndexRequest.current || key !== commentKeyRef.current) return;
        anchors.push(...page.anchors.map(anchor => ({ ...anchor, body: '', mentions: [], mine: false, can_delete: false })));
        cursor = page.next_cursor || undefined;
        if (cursor && cursors.has(cursor)) return;
        if (cursor) cursors.add(cursor);
      } while (cursor);
      commentWorkspaceRef.current.replaceAnchors(anchors);
    } catch { /* Retain existing pins while reconnecting. */ }
  }, [projectId, activeStage]);
  const acceptCommentThreads = useCallback((threads: StageComment[]) => {
    seenCommentIds.current.set(`${projectId}:${activeStage}`, new Set(threads.map(comment => comment.id)));
    commentWorkspaceRef.current.setThreads(threads);
    void refreshAnchorIndex();
  }, [projectId, activeStage, refreshAnchorIndex]);
  const loadCommentCounts = useCallback(async () => {
    try {
      const r = await commentsApi.counts(projectId);
      setCommentCounts(r.counts || {});
    } catch {
      /* counts are decoration */
    }
  }, [projectId]);
  useEffect(() => {
    void loadCommentCounts();
  }, [loadCommentCounts]);
  const refreshClosedComments = useCallback(async () => {
    const key = `${projectId}:${activeStage}`;
    if (commentsOpenRef.current || closedLoad.current === key || !commentStages.includes(activeStage)) return;
    closedLoad.current = key;
    try {
      const thread = await commentsApi.thread(projectId, activeStage);
      if (commentKeyRef.current !== key || commentsOpenRef.current) return;
      const previous = seenCommentIds.current.get(key);
      const incoming = previous && thread.comments.some(comment => !previous.has(comment.id) && !comment.mine && !comment.deleted && comment.author.id !== user?.id);
      acceptCommentThreads(thread.comments);
      if (incoming) closedCommentSoundRef.current();
      void loadCommentCounts();
    } catch { /* The drawer presents connection errors; existing pins stay visible. */ }
    finally { if (closedLoad.current === key) closedLoad.current = null; }
  }, [projectId, activeStage, commentStages, user?.id, acceptCommentThreads, loadCommentCounts]);
  useCommentUpdates(projectId, activeStage, () => void refreshClosedComments(), !commentsVisible && commentStages.includes(activeStage));
  useEffect(() => { if (!commentsVisible) void refreshClosedComments(); }, [commentsVisible, refreshClosedComments]);
  const commentTotal = commentCounts[activeStage] || 0;
  const canEstimate = estimateRights.view;

  // Architecture approval (DESIGN11 §P3.3) — the in-flight flag for the
  // «Approve architecture» button.
  const [approvingArch, setApprovingArch] = useState(false);
  // The authoritative four-stage pipeline snapshot for the open project
  // (DESIGN11 §P3). GET /projects/:id returns it as a TOP-LEVEL sibling of
  // `project` (it is NOT nested on the row like the list endpoint), so it
  // lives in its own state — refetched whenever the project meta is (after
  // runs finish and after the estimate/proposal modals close) and refreshed
  // by the approve response. null until the first fetch lands.
  const [pipeline, setPipeline] = useState<Pipeline | null>(null);
  const priorPipeline = useRef<{ estimate: string; proposal: string; estimateRun: boolean; proposalRun: boolean } | null>(null);
  const estimatePhase = pipeline?.estimate ?? 'none';
  const proposalPhase = pipeline?.proposal ?? 'none';
  const estimateRun = !!pipeline?.running?.estimate;
  const proposalRun = !!pipeline?.running?.proposal;
  // Approval can hand off to a generation run after the panel was opened.
  // A status-only poll must also refresh the visible document list and its
  // active-run indicator, while each editor protects a local dirty draft.
  useEffect(() => {
    if (!pipeline) return;
    const next = { estimate: estimatePhase, proposal: proposalPhase, estimateRun, proposalRun };
    const prior = priorPipeline.current;
    priorPipeline.current = next;
    if (!prior) return;
    if (prior.estimate !== next.estimate || prior.estimateRun !== next.estimateRun) {
      setDocumentRevision(value => value + 1);
      setPricingRevision(value => value + 1);
    }
    if (prior.proposal !== next.proposal || prior.proposalRun !== next.proposalRun) {
      setProposalRevision(value => value + 1);
    }
  }, [pipeline, estimatePhase, proposalPhase, estimateRun, proposalRun]);

  // A launch link must wait for the server's approval gates. Consuming it
  // before the project loads can mount a document from a locked stage.
  useEffect(() => {
    if (!pipeline || !pendingLaunch) return;
    if (pendingLaunch === 'comments') setCommentsOpen(true);
    else if (pendingLaunch !== 'poster') selectStage(pendingLaunch);
    setPendingLaunch(null);
  }, [pendingLaunch, pipeline, selectStage]);

  /* DESIGN23 §C3 — the architecture step's approval progress. Only fetched
     where the approve control lives (canGenerate); a failure is silent and
     leaves `status` null, which every helper below reads as «no policy» =
     today's behaviour. Reloaded after an approve and on the pipeline poll, so
     a colleague's vote in a 2-of-2 policy appears on its own. */
  const { status: archApprovals, reload: reloadArchApprovals } = useApprovalStatus(
    'architecture',
    'project',
    projectId,
    architectureRights.view,
  );

  const archActor = useApprovalActor({ root: access.isRoot, status: archApprovals, step: 'architecture', targetId: projectId, enabled: !!spec });
  const displayedArchitectureVersionId = displayedArchitecture?.projectId === projectId ? displayedArchitecture.versionId ?? undefined : undefined;
  const archReview = architectureReviewView(archApprovals);
  const archReviewStale = !!spec && (
    !displayedArchitecture || displayedArchitecture.projectId !== projectId || architectureDisplayLoading ||
    displayedArchitecture.versionId !== (versions[0]?.id ?? null) ||
    architectureReviewIsStale(archApprovals, displayedArchitectureVersionId)
  );
  useEffect(() => { reloadArchApprovals(); }, [reloadArchApprovals, displayedArchitectureVersionId]);
  const [architectureHistoryRequest, setArchitectureHistoryRequest] = useState(0);
  const workingRef = useRef<any>(null);
  const svgRef = useRef<SvgCanvasHandle | null>(null);
  const filesPanelRef = useRef<FilesPanelHandle | null>(null);
  const formBodyRef = useRef<HTMLDivElement | null>(null);
  // setInterval id of the active poll loop (0/undefined when idle).
  const pollRef = useRef<number | null>(null);
  // The run id the poll loop is CURRENTLY committed to (null when idle). A
  // Stop clears the run out from under an in-flight tick, and a new run may
  // start immediately after; each tick checks this before adopting so a stale
  // response from a superseded/stopped run can't clobber the live one.
  const pollRunIdRef = useRef<string | null>(null);
  const editorRootRef = useRef<HTMLDivElement | null>(null);
  const mqRef = useRef<MediaQueryList>(window.matchMedia('(max-width: 900px)'));

  useEffect(() => {
    const root = editorRootRef.current;
    const deck = root?.querySelector<HTMLElement>('.arch-stage-deck');
    if (!root || !deck) return;
    const measure = () => root.style.setProperty('--comment-drawer-top', `${Math.max(8, deck.getBoundingClientRect().top + 8)}px`);
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(deck);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [commentsVisible, activeStage]);

  // latest selPath, readable from long-lived async handlers
  const selPathRef = useRef<string | null>(selPath);
  useEffect(() => {
    selPathRef.current = selPath;
  }, [selPath]);

  // Keep the tree's reveal target = the most recent real selection, so a
  // clearSelection (◀ Structure) doesn't erase where the operator was.
  useEffect(() => {
    if (selPath) setRevealPath(selPath);
  }, [selPath]);

  // Pulse the edit-form container once when a CANVAS click opened it
  // (DESIGN9 §1), so the eye lands on the editor without hunting. Keyed
  // on a counter so it fires even when the same element is re-clicked.
  useEffect(() => {
    if (!panelPulse) return;
    const el = formBodyRef.current;
    if (!el) return;
    el.classList.remove('arch-panel-flash');
    void el.getBoundingClientRect(); // restart the animation
    el.classList.add('arch-panel-flash');
    const t = window.setTimeout(() => el.classList.remove('arch-panel-flash'), 650);
    return () => window.clearTimeout(t);
  }, [panelPulse]);

  // Persist the sources-collapse flag and, once the 240ms grid transition
  // settles, let the canvas re-write its transform for the new width
  // (never a refit — that would yank the operator's zoom).
  useEffect(() => {
    try {
      localStorage.setItem('arch.sourcesCollapsed', sourcesCollapsed ? '1' : '0');
      localStorage.setItem(
        'arch.structureCollapsed',
        structureCollapsed ? '1' : '0',
      );
    } catch {
      /* ignore */
    }
    setPeek(null); // a fold or a pin always ends a peek
    const t = window.setTimeout(() => svgRef.current?.reapplyView(), 300);
    return () => window.clearTimeout(t);
  }, [sourcesCollapsed, structureCollapsed]);

  /* ── hover-peek ─────────────────────────────────────────
     A short leave delay keeps the panel from flickering when the pointer
     crosses the 1px seam between the rail and the panel it slides out. */
  const openPeek = useCallback((side: 'left' | 'right') => {
    if (peekTimer.current !== null) {
      window.clearTimeout(peekTimer.current);
      peekTimer.current = null;
    }
    if (mqRef.current.matches) return; // ≤900px keeps its drawers
    setPeek(side);
  }, []);

  const closePeek = useCallback(() => {
    if (peekTimer.current !== null) window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => {
      peekTimer.current = null;
      setPeek(null);
    }, 200);
  }, []);

  useEffect(
    () => () => {
      if (peekTimer.current !== null) window.clearTimeout(peekTimer.current);
    },
    [],
  );

  /* Rail click — «this side, please»: it opens and the other folds away. */
  const focusSources = useCallback(() => {
    setPeek(null);
    setSourcesCollapsed(false);
    setStructureCollapsed(true);
  }, []);
  const focusStructure = useCallback(() => {
    setPeek(null);
    setStructureCollapsed(false);
    setSourcesCollapsed(true);
  }, []);

  /* «[» folds the sources, «]» the editor, Esc tucks a peeked panel away —
     desktop only, and never while a field has focus (the brackets are
     ordinary characters there). Keys are matched by physical position
     (e.code), so a Russian layout — х / ъ on those caps — works the same. */
  useEffect(() => {
    if (activeStage !== 'architecture') return;
    const onKey = (e: KeyboardEvent) => {
      const isBracket = e.code === 'BracketLeft' || e.code === 'BracketRight';
      if (!isBracket && e.key !== 'Escape') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (mqRef.current.matches) return;
      const t = e.target as HTMLElement | null;
      const tag = t ? t.tagName : '';
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (t && t.isContentEditable)
      ) {
        return;
      }
      if (!isBracket) {
        setPeek(null); // Esc: a no-op unless something is peeked
        return;
      }
      e.preventDefault();
      setPeek(null);
      if (e.code === 'BracketLeft') setSourcesCollapsed((v) => !v);
      else setStructureCollapsed((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeStage]);

  const bumpForm = useCallback(() => setFormRev((x) => x + 1), []);
  const markDirty = useCallback(() => { editRevision.current += 1; setDirty(true); }, []);

  const clearSelection = useCallback(() => {
    setSelPath(null);
    if (mqRef.current.matches) setRightOpen(false);
  }, []);

  const selectPath = useCallback(
    (path: string) => {
      const working = workingRef.current;
      if (!working) return;
      const kind = pathKind(path);
      const r = resolvePath(working, path);
      if (!kind || !r || r.value === undefined || r.value === null) {
        clearSelection();
        return;
      }
      setSelPath(path);
      if (mqRef.current.matches) {
        setLeftOpen(false);
        setRightOpen(true);
      } else {
        // Desktop: a click on the poster means «I want to edit this» — the
        // folded editor opens rather than swallowing the click.
        setStructureCollapsed((was) => {
          if (was) {
            setPeek(null);
            setSourcesCollapsed(true);
          }
          return false;
        });
      }
    },
    [clearSelection],
  );

  const refreshProjectMeta = useCallback(
    async (takeSpec = false, preserveView = true, failOnError = false) => {
      if (takeSpec) setArchitectureDisplayLoading(true);
      try {
        const r = await architectApi.getProject(projectId);
        setProject(r.project || null);
        setPipeline(r.pipeline || null);
        setFiles(r.files || []);
        setVersions(r.versions || []);
        if (takeSpec) {
          const svg = r.spec ? await loadArchitectureSnapshotSvg(projectId, r.spec, r.versions?.[0]?.id ?? null) : null;
          setSpec(r.spec || null);
          workingRef.current = r.spec ? deepCopy(r.spec) : null;
          if (svg != null) svgRef.current?.setSvg(svg, preserveView);
          setDirty(false);
          setQa(r.qa || null);
          setDisplayedArchitecture(r.spec && svg != null ? { projectId, versionId: r.versions?.[0]?.id ?? null, specFingerprint: r.spec_fingerprint, approved: r.project?.architecture_status === 'approved' } : null);
          bumpForm();
        }
      } catch (err: any) {
        if (takeSpec) setDisplayedArchitecture(null);
        toast(err.message, 'err');
        if (failOnError) throw err;
      } finally {
        if (takeSpec) setArchitectureDisplayLoading(false);
      }
    },
    [projectId, bumpForm],
  );

  /** Rename the project from the editor header (DESIGN18 §F1.3). One PATCH
      runs the server-side cascade; a rename is metadata, so it never touches
      the pipeline/approval — the strip stays put. On success reflect the new
      name at once, then refresh the project meta AND re-inline the poster SVG
      (the server re-rendered it in place with the new client name), preserving
      the operator's current view. The re-rendered spec is only adopted when
      there are no unsaved edits, so a rename never clobbers a dirty working
      copy. Only the PATCH itself throws — the caller (InlineRename) keeps the
      field open and toasts the mapped copy on failure; a failed SVG reload is
      swallowed since the rename already landed. Plain function (not memoised)
      so it always closes over the current `dirty`. */
  const renameHeader = async (newName: string) => {
    if (!architectureRightsRef.current.edit) return;
    const r = await architectApi.renameProject(projectId, newName);
    setProject((prev) => (prev ? { ...prev, name: r.project?.name ?? newName } : prev));
    if (r.pipeline) setPipeline(r.pipeline);
    await refreshProjectMeta(!dirty);
    try {
      const svg = await architectApi.getSvgText(projectId);
      svgRef.current?.setSvg(svg, true);
    } catch (e: any) {
      if (e.status !== 404) toast(e.message, 'err');
    }
    (r.warnings || []).forEach((w) => toast(w, 'err'));
  };

  /** Save the project number / doc_kind from the editor header (DESIGN20 §F1.3),
      sharing the ProjectCode popover with the card. Codes are computed, so this
      is pure metadata: NO cascade, NO poster re-render, NO approval touch — just
      merge the fresh number/kind (like renameHeader keeps its merge minimal so
      the inputs the FilesPanel reads aren't clobbered) and adopt any refreshed
      pipeline. A 409 (code_taken) throws back to the popover, which surfaces it
      inline. */
  const saveHeaderCode = async (patch: {
    number: number | null;
    doc_kind: DocKind;
  }) => {
    const r = await architectApi.patchProject(projectId, patch);
    const proj = r.project as ArchitectProject | undefined;
    setProject((prev) =>
      prev
        ? {
            ...prev,
            number: proj?.number ?? patch.number,
            doc_kind: proj?.doc_kind ?? patch.doc_kind,
          }
        : prev,
    );
    if (r.pipeline) setPipeline(r.pipeline);
    (r.warnings || []).forEach((w) => toast(w, 'err'));
  };

  /** Pipeline-ONLY refresh (DESIGN12 §F1) for the background liveness poll:
      updates just the pipeline snapshot, never touching project/files/versions
      state. That keeps the animated dots live without re-rendering the sources
      panel or versions menu on every tick, and stays silent on failure (a
      background poll must not surface toasts). */
  const refreshPipeline = useCallback(async () => {
    // DESIGN23 §C3 — piggyback the approval progress on the SAME cadence, so
    // «Approved 1 of 2» follows a colleague's vote without its own timer.
    reloadArchApprovals();
    try {
      const r = await architectApi.getProject(projectId);
      setPipeline(r.pipeline || null);
    } catch {
      /* transient — the next tick tries again */
    }
  }, [projectId, reloadArchApprovals]);

  /* ── keep the pipeline strip live (DESIGN12 §F1, DESIGN17 §F1.2) ────────
     A module run can outlive its modal — the estimate/proposal panels tell
     the user «you can leave this page», so a run may finish while the editor
     bar is on screen with no active run of its own.

     DESIGN17 makes this poll load-bearing, not cosmetic: the two document
     surfaces are compartmentalised, so the OTHER role's progress reaches
     this editor ONLY through the pipeline snapshot (statuses, every role).
     The Proposal button is gated on `estimate === 'approved'`, and approving
     an estimate is an instantaneous action with NO run attached — no
     `running` flag would ever bring the fresh status in. So the pipeline is
     polled the whole time the editor is open: fast (5s) while any module run
     is live so the animated dot clears itself, slow (15s) when idle so the
     Proposal button appears within seconds of the estimator's approval. The
     request carries statuses only and stops with the editor. Skipped while
     THIS tab drives an architect run (activeRun refetches on completion). */
  const pipeRunning = pipeline?.running;
  const anyPipelineRunning = !!(
    pipeRunning &&
    (pipeRunning.architecture || pipeRunning.estimate || pipeRunning.proposal)
  );
  const pipelinePollMs = anyPipelineRunning ? 5000 : 15000;
  useEffect(() => {
    if (activeRun) return undefined;
    const t = window.setInterval(() => {
      // Nothing to see while the tab is in the background — the
      // visibility/focus handler below catches up on return.
      if (document.visibilityState === 'hidden') return;
      void refreshPipeline();
    }, pipelinePollMs);
    return () => window.clearInterval(t);
  }, [pipelinePollMs, activeRun, refreshPipeline]);

  /* Refresh the pipeline the moment the operator returns to the tab, so an
     estimate approved while this tab sat in the background is reflected at
     once instead of waiting out the idle interval. */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refreshPipeline();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [refreshPipeline]);

  /** Approve the current architecture (DESIGN11 §P3.3, DESIGN23 §C3). Behind
      a confirm; canGenerate is enforced both here (button visibility) and on
      the server, which additionally checks the configured approver list.

      TWO outcomes now:
        • threshold MET (the default — no policy means one approval) → today's
          exact path: the project flips to 'approved', the strip + button→chip
          swap follow, and architecture='approved' is pinned defensively in
          case a partial response omitted the pipeline.
        • vote RECORDED below the threshold (only possible with a configured
          multi-approver policy) → the server deliberately left the status at
          'draft'. Nothing may be pinned here: adopt the response as-is and
          say how far it has got. Any later poster edit sends it back to draft
          (server-driven, reflected on the next project refetch). */
  const approveArchitecture = useCallback(async () => {
    if (!architectureRightsRef.current.approve || (architectureRightsRef.current.configured && !archApprovals) || !archActor.canApprove || approvingArch || running || dirty || applying || archReviewStale) return;
    setApprovingArch(true);
    try {
      const r = await architectApi.approveArchitecture(projectId, { ...archActor.body, ...architectureReviewVersion(archApprovals, displayedArchitectureVersionId) });
      // An absent `approvals` block is an older/partial backend — that is the
      // single-approval world, so treat it as met (never leave the UI stuck
      // in a half-approved state that the server doesn't believe in).
      const progress = r.approvals;
      const met = !progress || progress.given >= progress.required;
      setProject((prev) => {
        const base: any = prev ? { ...prev, ...r.project } : { ...r.project };
        if (met) base.architecture_status = 'approved';
        return base;
      });
      // Prefer the server's refreshed snapshot; if it was omitted, pin only
      // the architecture stage on the last-known pipeline (approval doesn't
      // move the estimate/proposal/priced stages) — and only once it flipped.
      setPipeline((prev) =>
        r.pipeline ??
        (met && prev ? { ...prev, architecture: 'approved' } : prev),
      );
      reloadArchApprovals();
      // DESIGN27 — the approval starts the estimate by itself. Say so, and
      // refetch so the estimate stage flips to «running» now instead of on the
      // next poll. `auto_started` is optional: an older backend omits it and
      // this reads exactly as it did before.
      const autoEstimate = r.auto_started === 'estimate';
      toast(
        met
          ? autoEstimate
            ? 'Architecture approved — the estimate started automatically'
            : 'Architecture approved'
          : archReview?.submitted || 'Your approval is recorded — ' +
              progress!.given +
              ' of ' +
              progress!.required,
        'ok',
      );
      if (autoEstimate) {
        if (canEstimate) setNextStageAfterApproval('estimate');
        void refreshProjectMeta();
      }
    } catch (err: any) {
      toast(err.message, 'err');
      reloadArchApprovals();
      // 409 = another session already approved it — re-sync so the stale
      // button swaps for the approved chip instead of lingering dead.
      if (err.status === 409) void refreshProjectMeta();
    } finally {
      setApprovingArch(false);
    }
  }, [projectId, refreshProjectMeta, reloadArchApprovals, canEstimate, approvingArch, running, dirty, applying, archActor.body, archReviewStale, archReview?.submitted, archApprovals, archActor.canApprove, displayedArchitectureVersionId]);

  /** Shared handling of the generate / prompt / restore response. */
  const adoptRenderResult = useCallback(
    (r: RenderResult, preserveView: boolean) => {
      if (r.project) setProject(r.project);
      if (r.pipeline) setPipeline(r.pipeline);
      if (r.spec) {
        setSpec(r.spec);
        workingRef.current = deepCopy(r.spec);
        setDirty(false);
      }
      setQa(r.qa || null);
      if (r.svg) svgRef.current?.setSvg(r.svg, preserveView);
      if (r.version) {
        const v = r.version;
        setVersions((prev) => [v, ...prev]);
      }
      if (r.spec && r.svg && r.version) {
        setDisplayedArchitecture({ projectId, versionId: r.version.id, specFingerprint: r.spec_fingerprint || r.version.spec_fingerprint, approved: r.project?.architecture_status === 'approved' });
      } else {
        setDisplayedArchitecture(null);
        void refreshProjectMeta(true);
      }
      if (r.telemetry) setTelemetry(r.telemetry);
      bumpForm();
      const cur = selPathRef.current;
      if (cur) {
        const rr = resolvePath(workingRef.current, cur);
        if (rr && rr.value !== undefined && rr.value !== null) selectPath(cur);
        else clearSelection();
      } else {
        clearSelection();
      }
    },
    [projectId, bumpForm, refreshProjectMeta, selectPath, clearSelection],
  );

  /* ── run lifecycle (async generate/prompt) ───────────────
     Generation is a tracked RUN: POST returns a run immediately and we
     poll GET /runs/:id until done/error. This survives proxy timeouts
     and — via getActiveRun on open — page reloads. */

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const clearRun = useCallback(() => {
    stopPolling();
    pollRunIdRef.current = null;
    setRunning(false);
    setActiveRun(null);
    setRunStartMs(null);
  }, [stopPolling]);

  /** A run finished successfully: refetch the project (spec/versions/
      files/qa), re-inline the fresh SVG, and adopt the run's telemetry.
      Generate fits the new poster to width; prompt preserves the view. */
  const adoptFinishedRun = useCallback(
    async (run: Run) => {
      await refreshProjectMeta(true, run.kind === 'prompt');
      if (run.telemetry) setTelemetry(run.telemetry as Telemetry);
      const cur = selPathRef.current;
      if (cur) {
        const rr = resolvePath(workingRef.current, cur);
        if (rr && rr.value !== undefined && rr.value !== null) selectPath(cur);
        else clearSelection();
      } else {
        clearSelection();
      }
    },
    [refreshProjectMeta, selectPath, clearSelection],
  );

  const beginPolling = useCallback(
    (runId: string) => {
      stopPolling();
      pollRunIdRef.current = runId;
      // Reentrancy guard: a GET slower than the poll interval means two
      // ticks can be in flight at once — without this, both could see
      // the terminal status and adopt/toast twice.
      let inFlight = false;
      let settled = false;
      const tick = async () => {
        if (inFlight || settled) return;
        inFlight = true;
        let run: Run;
        try {
          run = (await architectApi.getRun(runId)).run;
        } catch (e: any) {
          inFlight = false;
          // A lost run (404) is terminal; transient errors keep polling.
          if (e.status === 404) {
            settled = true;
            clearRun();
            toast('The generation run was lost — try again', 'err');
          }
          return;
        }
        inFlight = false;
        // The run was stopped (or superseded by a new one) while this GET was
        // in flight — drop the stale response so it can't re-show or clobber.
        if (settled || pollRunIdRef.current !== runId) return;
        setActiveRun(run);
        if (run.status === 'done') {
          settled = true;
          stopPolling();
          await adoptFinishedRun(run);
          toast(
            run.kind === 'generate'
              ? 'Poster generated'
              : 'Poster updated from the prompt',
            'ok',
          );
          clearRun();
        } else if (run.status === 'error') {
          settled = true;
          clearRun();
          // An operator stop is not a failure — clear the error banner and
          // give it a calm neutral toast (this reaches here when the run was
          // stopped in ANOTHER tab and arrives via the poll; §F4).
          if (isOperatorStop(run.error)) {
            setRunError(null);
            toast('Generation stopped');
          } else {
            const msg = runErrorText(run.error);
            setRunError(msg);
            toast(msg, 'err');
          }
        }
      };
      tick(); // poll immediately, then on the interval
      pollRef.current = window.setInterval(tick, RUN_POLL_MS);
    },
    [stopPolling, clearRun, adoptFinishedRun],
  );

  /** Kick off a generate or prompt run and start polling. Returns true
      once the run has been accepted by the server (so the prompt bar can
      clear its input). Model is normalised to a supported value. */
  const startRun = useCallback(
    async (
      kind: 'generate' | 'prompt',
      params?: { instruction: string },
    ): Promise<boolean> => {
      if (!architectureRightsRef.current.edit || running) return false;
      let guard: ArchitectureEditRequest | undefined;
      try {
        guard = editGuard('structural');
        if (editingApprovedArchitecture && !await requestStructuralEdit(kind === 'generate' ? 'Regenerate architecture?' : 'Apply AI changes?', kind === 'generate' ? 'Generate architecture' : 'Apply AI changes')) return false;
      } catch (err: any) { toast(err.message, 'err'); return false; }
      const sendModel = MODELS.some((m) => m.value === model)
        ? model
        : MODELS[0].value;
      setRunning(true);
      setRunError(null); // a fresh attempt clears the previous failure
      if (mqRef.current.matches) setLeftOpen(false);
      try {
        // Commit every pending sources autosave (transcript / phases /
        // file notes / extra) BEFORE the run starts — the server job
        // reads inputs from the DB, so an unflushed debounce would make
        // this run use stale data.
        try {
          await filesPanelRef.current?.flushAllPending();
        } catch {
          /* saves surface their own toasts */
        }
        const { run } =
          kind === 'generate'
            ? await architectApi.generate(projectId, { model: sendModel, ...guard })
            : await architectApi.promptRun(projectId, {
                instruction: params!.instruction,
                model: sendModel,
                ...guard,
              });
        setActiveRun(run);
        setRunStartMs(Date.parse(run.created_at) || Date.now());
        beginPolling(run.id);
        return true;
      } catch (err: any) {
        toast(err.message, 'err');
        setRunning(false);
        return false;
      }
    },
    [running, model, projectId, beginPolling, editGuard, requestStructuralEdit, editingApprovedArchitecture],
  );

  /** Stop the active generate/prompt run (DESIGN13 §F3). Confirmed inside the
      stepper's button. Cancel is idempotent server-side and returns the
      updated run: if it came back stopped, clear the overlay immediately and
      give a neutral toast; if it had already reached a terminal state on its
      own, leave the poll loop to adopt it (success) or surface it. */
  const stopRun = useCallback(async () => {
    const run = activeRun;
    if (!run) return;
    try {
      const { run: stopped } = await architectApi.cancelRun(run.id);
      if (isOperatorStop(stopped.error)) {
        clearRun();
        setRunError(null);
        toast('Generation stopped');
      } else {
        // Raced with a natural finish — hand it back to the poll loop.
        setActiveRun(stopped);
      }
    } catch (err: any) {
      toast(err.message, 'err');
    }
  }, [activeRun, clearRun]);

  /* ── open flow ───────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    setDisplayedArchitecture(null);
    setArchitectureDisplayLoading(true);
    (async () => {
      try {
        const r = await architectApi.getProject(projectId);
        if (cancelled) return;
        setProject(r.project);
        setPipeline(r.pipeline || null);
        setFiles(r.files || []);
        setVersions(r.versions || []);
        setSpec(r.spec || null);
        workingRef.current = r.spec ? deepCopy(r.spec) : null;
        setDirty(false);
        setQa(r.qa || null);
        setSelPath(null);
        setTelemetry(null);
        bumpForm();
        if (r.spec) {
          try {
            const svg = await loadArchitectureSnapshotSvg(projectId, r.spec, r.versions?.[0]?.id ?? null);
            if (!cancelled) {
              svgRef.current?.setSvg(svg, false);
              setDisplayedArchitecture({ projectId, versionId: r.versions?.[0]?.id ?? null, specFingerprint: r.spec_fingerprint, approved: r.project?.architecture_status === 'approved' });
            }
          } catch (e: any) {
            if (!cancelled && e.status !== 404) toast(e.message, 'err');
          }
        }
        // RELOAD FIX: if a generation is still in flight on the server,
        // resume the progress overlay instead of showing an idle editor.
        try {
          const { run } = await architectApi.getActiveRun(projectId);
          if (!cancelled && run && run.status === 'running') {
            setActiveRun(run);
            setRunStartMs(Date.parse(run.created_at) || Date.now());
            setRunning(true);
            beginPolling(run.id);
          }
        } catch (e: any) {
          /* no active-run info — nothing to resume */
        }
      } catch (err: any) {
        if (cancelled) return;
        toast(err.message, 'err');
        onBack();
      } finally {
        if (!cancelled) setArchitectureDisplayLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /* ── anchor the editor height to its real position ───────
     The editor is embedded in the scrolling admin page, so a fixed
     viewport-relative height mis-fits when the chrome above it changes
     (slim Architect shell vs the full dashboard with the stats panel).
     Measure the editor's document offset (scroll-independent) and fill
     from there down to a small gap, so the prompt bar stays on-screen
     in place. Clamped so it never collapses on short viewports. */
  useEffect(() => {
    const el = editorRootRef.current;
    if (!el) return;
    let raf = 0;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      const docTop = rect.top + window.scrollY; // stable across scroll
      const bottomGap = el.closest('.architect-workspace') ? 0 : 20;
      const h = Math.max(420, Math.round(window.innerHeight - docTop - bottomGap));
      el.style.height = h + 'px';
    };
    // rAF so layout (fonts, stats panel) has settled before measuring.
    raf = window.requestAnimationFrame(apply);
    window.addEventListener('resize', apply);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', apply);
    };
  }, []);

  /* ── elapsed ticker while a run is active ────────────────
     Based on the run's wall-clock start (run.created_at) so a resumed
     run shows the real elapsed time, not time-since-reopen. */
  useEffect(() => {
    if (runStartMs == null) {
      setElapsedMs(0);
      return;
    }
    const upd = () => setElapsedMs(Math.max(0, Date.now() - runStartMs));
    upd();
    const t = setInterval(upd, 1000);
    return () => clearInterval(t);
  }, [runStartMs]);

  /* ── close drawers when leaving the mobile breakpoint ── */
  useEffect(() => {
    const mq = mqRef.current;
    const onChange = () => {
      setPeek(null);
      if (!mq.matches) {
        setLeftOpen(false);
        setRightOpen(false);
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  /* ── files ───────────────────────────────────────────── */
  const uploadFiles = useCallback(
    async (fileList: FileList | File[]) => {
      const arr = Array.from(fileList);
      if (!arr.length) return;
      if (arr.length > 10) {
        toast('No more than 10 files at once', 'err');
        return;
      }
      const tooBig = arr.find((f) => f.size > 25 * 1024 * 1024);
      if (tooBig) {
        toast('File "' + tooBig.name + '" is over 25 MB', 'err');
        return;
      }
      const fd = new FormData();
      for (const f of arr) fd.append('files', f, f.name);
      try {
        const r = await architectApi.uploadFiles(projectId, fd);
        if (Array.isArray(r.files)) {
          const added = r.files;
          setFiles((prev) => prev.concat(added));
        } else {
          await refreshProjectMeta();
        }
        toast('Files uploaded: ' + arr.length, 'ok');
      } catch (err: any) {
        toast(err.message, 'err');
      }
    },
    [projectId, refreshProjectMeta],
  );

  const deleteFile = useCallback(
    async (f: FileMeta) => {
      if (!window.confirm('Delete file "' + f.filename + '"?')) return;
      try {
        await architectApi.deleteFile(projectId, f.id);
        setFiles((prev) => prev.filter((x) => x.id !== f.id));
      } catch (err: any) {
        toast(err.message, 'err');
      }
    },
    [projectId],
  );

  /* ── generate / prompt (run-based) ───────────────────── */
  const generate = useCallback(() => {
    void startRun('generate');
  }, [startRun]);

  const promptSend = useCallback(
    async (raw: string): Promise<boolean> => {
      if (!architectureRightsRef.current.edit || running) return false;
      const instruction = raw.trim();
      if (!instruction) {
        toast('Describe what to change on the poster', 'err');
        return false;
      }
      if (!spec) {
        toast('Generate the poster first', 'err');
        return false;
      }
      return startRun('prompt', { instruction });
    },
    [running, spec, startRun],
  );

  /** Sync a saved file note back into local state so a later refetch and
      re-render keep it. */
  const onFileNoteSaved = useCallback((fileId: string, note: string) => {
    setFiles((prev) =>
      prev.map((f) => (f.id === fileId ? { ...f, note } : f)),
    );
  }, []);

  /* ── apply / discard ─────────────────────────────────── */
  const saveSpec = useCallback(async (nextSpec: Spec) => {
    if (!architectureRightsRef.current.edit) return false;
    try {
      const guard = editGuard(editMode);
      if (editingApprovedArchitecture && guard?.edit_mode === 'structural' && !await requestStructuralEdit('Save structural changes?', 'Save structural changes')) return false;
      setApplying(true);
      setArchitectureSaveError(null);
      const submittedRevision = editRevision.current;
      const cleaned = cleanSpec(nextSpec);
      const r = guard ? await architectApi.putSpec(projectId, cleaned, guard) : await architectApi.putSpec(projectId, cleaned);
      const hasNewerDraft = editRevision.current !== submittedRevision;
      if (r.project) setProject(r.project);
      if (r.pipeline) setPipeline(r.pipeline);
      setSpec(cleaned);
      if (!hasNewerDraft) workingRef.current = deepCopy(cleaned);
      setDirty(hasNewerDraft);
      setQa(r.qa || null);
      if (r.svg) svgRef.current?.setSvg(r.svg, true);
      if (r.version) {
        const v = r.version;
        setVersions((prev) => [v, ...prev]);
      }
      if (r.svg && r.version) {
        setDisplayedArchitecture({ projectId, versionId: r.version.id, specFingerprint: r.spec_fingerprint || r.version.spec_fingerprint, approved: r.project?.architecture_status === 'approved' || guard?.edit_mode === 'wording' });
      } else {
        setDisplayedArchitecture(null);
        await refreshProjectMeta(!hasNewerDraft);
      }
      if (!hasNewerDraft) bumpForm();
      const cur = selPathRef.current;
      if (cur && !hasNewerDraft) {
        const rr = resolvePath(workingRef.current, cur);
        if (rr && rr.value !== undefined && rr.value !== null) {
          selectPath(cur);
          // pulse the just-edited element on the freshly re-rendered SVG
          svgRef.current?.flashPath(cur);
        } else clearSelection();
      }
      // Wording retains the approved scope; structural edits reopen it.
      void refreshProjectMeta();
      toast(guard?.edit_mode === 'wording' ? 'Wording saved. The approval flow is unchanged.' : 'Changes applied', 'ok');
      return true;
    } catch (err: any) {
      setArchitectureSaveError(err.message || 'Changes could not be saved. Your draft is still here.');
      throw err;
    } finally {
      setApplying(false);
    }
  }, [projectId, refreshProjectMeta, bumpForm, selectPath, clearSelection, editMode, editGuard, requestStructuralEdit, editingApprovedArchitecture]);

  const apply = useCallback(async () => {
    if (!workingRef.current || applying || running) return;
    try { await saveSpec(workingRef.current); }
    catch (err: any) { toast(err.message, 'err'); }
  }, [saveSpec, applying, running]);

  const applyText = useCallback(async (target: InlineTextTarget, value: string) => {
    if (!access.canGenerate || applying || running) throw new Error('Editing is unavailable while the document is updating.');
    // Save a copy: a rejected render must not change the existing draft or SVG.
    return saveSpec(replaceInlineText(workingRef.current, target, value));
  }, [access.canGenerate, applying, running, saveSpec]);

  const discard = useCallback(() => {
    if (!spec) return;
    workingRef.current = deepCopy(spec);
    setDirty(false);
    bumpForm();
    const cur = selPathRef.current;
    if (cur) {
      const rr = resolvePath(workingRef.current, cur);
      if (rr && rr.value !== undefined && rr.value !== null) selectPath(cur);
      else clearSelection();
    }
    toast('Edits discarded');
  }, [spec, bumpForm, selectPath, clearSelection]);

  /* ── versions restore ────────────────────────────────── */
  const restore = useCallback(
    async (v: VersionMeta) => {
      if (!architectureRightsRef.current.edit) return;
      try {
        const guard = editGuard('structural');
        if (editingApprovedArchitecture ? !await requestStructuralEdit('Restore version v' + v.seq + '?', 'Restore version')
          : !window.confirm('Restore version v' + v.seq + ' (' + (SOURCE_EN[v.source] || v.source) + ')?')) return;
        const r = guard ? await architectApi.restore(projectId, v.id, guard) : await architectApi.restore(projectId, v.id);
        if (r.version && r.version.spec) {
          adoptRenderResult(
            { ...r, spec: r.version.spec },
            true,
          );
        } else {
          if (r.svg) svgRef.current?.setSvg(r.svg, true);
          setQa(r.qa || null);
          await refreshProjectMeta(true);
          clearSelection();
        }
        // A restore writes a new poster version → architecture back to draft
        // (DESIGN11 §P1.3). Refetch so the pipeline strip reflects it.
        void refreshProjectMeta();
        toast('Version v' + v.seq + ' restored', 'ok');
      } catch (err: any) {
        toast(err.message, 'err');
      }
    },
    [projectId, adoptRenderResult, refreshProjectMeta, clearSelection, editGuard, requestStructuralEdit, editingApprovedArchitecture],
  );

  /* ── export PDF ──────────────────────────────────────── */
  const exportPdf = useCallback(async () => {
    if (!spec) {
      toast('Generate the poster first', 'err');
      return;
    }
    setExporting(true);
    try {
      const res = await architectApi.exportPdf(projectId);
      const blob = await res.blob();
      let filename = slugName(project?.name);
      const cd = res.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
      if (m) filename = decodeURIComponent(m[1]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast('PDF downloaded', 'ok');
    } catch (err: any) {
      toast(err.message, 'err');
    } finally {
      setExporting(false);
    }
  }, [spec, projectId, project]);

  const onCanvasSelect = useCallback(
    (path: string | null) => {
      if (path) {
        // Canvas click: the operator is already looking at it — flash
        // only, never re-frame the view under their cursor. Pulse the
        // form container so the editor that just opened catches the eye.
        selectPath(path);
        svgRef.current?.flashPath(path);
        setPanelPulse((n) => n + 1);
      } else {
        clearSelection();
      }
    },
    [selectPath, clearSelection],
  );

  // Selecting from the Structure tree or the breadcrumb also frames and
  // pulses the element on the canvas (FEUX3: tree selects zoom, clicks
  // don't). ContextEditor keeps its plain selectPath (no zoom).
  const navSelect = useCallback(
    (path: string) => {
      selectPath(path);
      svgRef.current?.zoomToPath(path);
      svgRef.current?.flashPath(path);
    },
    [selectPath],
  );

  const onTreeHover = useCallback((path: string | null) => {
    svgRef.current?.setHoverPath(path);
  }, []);

  /* ── derived UI values ───────────────────────────────── */
  // Breadcrumb + kind description for the selected element (FEUX3). The
  // crumb walks the selPath ancestors; each is clickable via navSelect.
  const crumbs = selPath ? crumbSegments(workingRef.current, selPath) : [];
  const selKind = selPath ? pathKind(selPath) : null;
  const kindDesc = selKind ? KIND_DESC[selKind] : '';
  const qaMsg = qaText(qa);
  const anyDrawer = leftOpen || rightOpen;

  /* Chevron in the editor panel's head: folds it away when open, pins a peek
     open when folded (pinning never touches the sources panel, so a wide
     screen can keep both). Desktop only — CSS hides it under 901px. */
  const structureToggle = (
    <button
      type="button"
      className="arch-panel-collapse"
      title={structureCollapsed ? 'Keep the editor open' : 'Hide the editor'}
      aria-label={structureCollapsed ? 'Keep the editor open' : 'Hide the editor'}
      onClick={() => {
        setPeek(null);
        setStructureCollapsed(!structureCollapsed);
      }}
    >
      <PanelRight size={16} aria-hidden="true" />
    </button>
  );
  const runKind = activeRun ? activeRun.kind : null;
  // Structured generation inputs live on the project record (added by
  // the inputs endpoints); default to empty until it loads.
  const initialTranscript = (project?.main_transcript as string) || '';
  const initialExtra = (project?.extra_notes as string) || '';
  const initialPhases = (project?.phases as Phase[]) || [];

  // Architecture-approval + pipeline snapshot (DESIGN11 §P3). The project
  // record carries these tolerantly (open index signature); fall back to a
  // spec-derived pipeline until the server's authoritative one arrives.
  const archProject = project as ArchitectProject | null;
  const archStatus = archProject?.architecture_status;
  // Prefer the server's authoritative snapshot; until it lands, fall back to
  // a spec-derived minimal pipeline that at least knows the architecture
  // stage (estimate/proposal/priced show as not-started until the fetch).
  const onEstimateChanged = useCallback(() => {
    setDocumentRevision((value) => value + 1);
    setPricingRevision((value) => value + 1);
    setProposalRevision((value) => value + 1);
    void refreshProjectMeta();
  }, [refreshProjectMeta]);
  const onPricingChanged = useCallback(() => {
    setProposalRevision((value) => value + 1);
    void refreshProjectMeta();
  }, [refreshProjectMeta]);
  const onProposalChanged = useCallback(() => {
    setPricingRevision((value) => value + 1);
    void refreshProjectMeta();
  }, [refreshProjectMeta]);
  const pipelineView: Pipeline = pipeline || {
    architecture: spec ? (archStatus === 'approved' ? 'approved' : 'draft') : 'none',
    estimate: 'none',
    proposal: 'none',
    priced: false,
  };
  const estimateApprovalStatus = useEstimateApprovalGate(projectId, pipeline, canEstimate);
  const estimateGate = estimateGateView(pipeline, estimateApprovalStatus, user?.id);


  // The server owns the conveyor gates. Keep a status-derived fallback for
  // older pods during rollout, but never treat a saved pricing draft as an
  // approval. Every path into a stage uses this same map (tabs, launch links,
  // pane actions), so a disabled tab cannot be bypassed programmatically.
  const approvalGates = approvedStageGates(pipeline);
  const staleEstimate = pipeline?.architecture === 'approved' && pipeline.estimate === 'none' && !!pipeline.historical?.estimate;
  const historicalEstimate = pipelineHasHistoricalDocument(pipeline, 'estimate') && (!approvalGates.estimate || staleEstimate) && canEstimate;
  const historicalProposal = pipelineHasHistoricalDocument(pipeline, 'proposal') && !approvalGates.proposal && canProposal;
  const historicalPricing = pipelineHasHistoricalDocument(pipeline, 'pricing') && !approvalGates.pricing && canPricing;
  const hasEstimateHistory = canEstimate && pipelineHasHistoricalDocument(pipeline, 'estimate');
  const hasProposalHistory = canProposal && pipelineHasHistoricalDocument(pipeline, 'proposal');
  const stageHistory = { estimate: historicalEstimate, pricing: historicalPricing, proposal: historicalProposal };
  const stageAvailable = useMemo<Record<WorkspaceStage, boolean>>(() => ({
    architecture: architectureRights.view,
    estimate: historicalEstimate || (canEstimate && approvalGates.estimate && !running),
    pricing: historicalPricing || (canPricing && approvalGates.pricing && !running),
    proposal: historicalProposal || (canProposal && approvalGates.proposal && !running),
  }), [architectureRights.view, historicalEstimate, historicalPricing, historicalProposal, canEstimate, canPricing, canProposal, approvalGates.estimate, approvalGates.pricing, approvalGates.proposal, running]);
  const stageReasons: Partial<Record<WorkspaceStage, string>> = {
    estimate: !canEstimate ? 'Estimate access is required' : running ? 'Wait for architecture generation to finish' : 'Approve the architecture first',
    pricing: !canPricing ? 'Pricing access is required' : running ? 'Wait for architecture generation to finish' : estimateGate?.reason || 'Approve the current estimate first',
    proposal: !canProposal ? 'Proposal access is required' : running ? 'Wait for architecture generation to finish' : 'Approve pricing first',
  };
  const readingSavedHistory = !!recoveryProposalId || legacyHistoryOpen || (activeStage !== 'architecture' && stageHistory[activeStage])
    || (pendingLaunch && pendingLaunch !== 'poster' && pendingLaunch !== 'comments' && stageHistory[pendingLaunch]);
  const pipelineResume = usePipelineResume({
    projectId, pipeline,
    revision: archProject?.architecture_approved_at || 'legacy-approved-scope',
    enabled: (estimateRights.edit || access.canGenerate) && !readingSavedHistory,
    onRefresh: async (current, started) => {
      setPipeline(current);
      if (started === 'pricing') setPricingRevision(value => value + 1);
      await refreshProjectMeta();
    },
  });
  useEffect(() => { if (pipelineResume.error) toast(pipelineResume.error, 'err'); }, [pipelineResume.error]);
  stageGateRef.current = stageAvailable;
  stageReasonRef.current = stageReasons;
  useEffect(() => {
    if (!pipeline || stageAvailable[activeStage]) return;
    const next = (['architecture', 'estimate', 'pricing', 'proposal'] as WorkspaceStage[]).find(stage => stageAvailable[stage]);
    if (next) setActiveStage(next);
    setCommentsOpen(false);
  }, [pipeline, activeStage, stageAvailable]);
  useEffect(() => {
    if (!nextStageAfterApproval) return;
    const source = nextStageAfterApproval === 'estimate' ? 'architecture' : 'pricing';
    if (activeStage !== source) { setNextStageAfterApproval(null); return; }
    if (!stageAvailable[nextStageAfterApproval]) return;
    selectStage(nextStageAfterApproval);
    setNextStageAfterApproval(null);
  }, [nextStageAfterApproval, activeStage, stageAvailable, selectStage]);

  return (
    <CommentAnchorProvider value={commentWorkspace}>
    <div className={'arch-editor arch-unified-workspace' + (commentsVisible ? ' comments-open' : '')} data-active-stage={activeStage} ref={editorRootRef}>
      <CommentPickingNotice />
      <header className="arch-editor-header">
        <div className="arch-project-navigation">
          <button
            className="arch-btn arch-btn-ghost arch-editor-back"
            aria-label="Back to projects"
            title="Back to projects"
            onClick={leaveWorkspace}
          >
            <ArrowLeft size={17} aria-hidden="true" />
          </button>
          <span className="arch-project-nav-divider" aria-hidden="true" />
          {project ? (
            <div className="arch-editor-title-group">
              <span className="arch-project-icon" aria-hidden="true"><FolderOpen size={18} /></span>
              <div className="arch-project-identity">
                {access.canGenerate ? <InlineRename
                  variant="header"
                  value={project.name}
                  onSubmit={renameHeader}
                  pencilLabel="Rename project"
                /> : <strong>{project.name}</strong>}
                <div className="arch-project-meta">
                  <span className="arch-project-type">Project workspace</span>
                  <span className="arch-project-meta-dot" aria-hidden="true">·</span>
                  {access.canGenerate && <ProjectCode
                    variant="header"
                    number={archProject?.number}
                    docKind={archProject?.doc_kind}
                    pipeline={pipelineView}
                    onSave={saveHeaderCode}
                  />}
                </div>
              </div>
            </div>
          ) : (
            <div className="arch-editor-title">…</div>
          )}

        </div>
        {project && (<WorkspaceStageNav active={activeStage} pipeline={pipelineView}
            available={stageAvailable}
            historical={stageHistory}
            disabledReason={stageReasons}
            onSelect={selectStage} />)}
        <div className="arch-editor-header-right">
          {hasEstimateHistory || hasProposalHistory ? <button type="button" className="arch-btn arch-btn-outline arch-comments-btn" aria-label="Document history" title="Document history" onClick={() => setLegacyHistoryOpen(true)}><History size={16} aria-hidden="true" /><span className="arch-comments-label">History</span></button> : null}

          <button
            className="arch-btn arch-btn-outline arch-comments-btn"
            title={`Comments on ${activeStage}`}
            disabled={!commentStages.includes(activeStage)}
            aria-label={commentTotal ? `Comments on ${activeStage} (${commentTotal})` : `Comments on ${activeStage}`}
            onClick={() => setCommentsOpen(true)}
          >
            <MessageSquare size={16} aria-hidden="true" />
            <span className="arch-comments-label">Comments</span>
            {commentTotal ? <span className="arch-comments-count">{commentTotal}</span> : null}
          </button>

        </div>
      </header>


      <div className="arch-stage-deck">
      {architectureRights.view && <WorkspaceScene stage="architecture" active={activeStage === 'architecture'}>
      <StageHeader title="Architecture" status={running ? 'Generating…' : dirty ? 'Unsaved changes' : archStatus === 'approved' ? 'Approved' : archReview?.badge || 'Draft'} actions={[
        ...(access.canGenerate ? [{ id: 'sources', label: 'Sources', icon: <PanelLeft size={17}/>, onClick: () => { if (window.innerWidth > 900) setSourcesCollapsed(value => !value); else { setRightOpen(false); setLeftOpen(value => !value); } } },
        { id: 'structure', label: 'Structure', icon: <PanelRight size={17}/>, onClick: () => { if (window.innerWidth > 900) setStructureCollapsed(value => !value); else { setLeftOpen(false); setRightOpen(value => !value); } } }] : []),
        { id: 'versions', label: 'Version history', icon: <History size={17}/>, onClick: () => setArchitectureHistoryRequest(value => value + 1) },
        { id: 'download', label: 'Download PDF', icon: <Download size={17}/>, disabled: running || exporting, onClick: () => { void exportPdf(); } },
        ...((historicalEstimate || historicalProposal) ? [{ id: 'earlier', label: 'Earlier documents', icon: <History size={17}/>, onClick: () => setLegacyHistoryOpen(true) }] : []),
      ]} decision={<StageFooter placement="header" stage="architecture" status={archApprovals} userId={user?.id} pending={archStatus !== 'approved'} title={archStatus === 'approved' ? 'Architecture approved' : !spec ? 'Architecture not started' : dirty ? 'Changes ready to save' : archReview?.title || 'Ready for review'} hint={dirty ? 'Save before approving' : architectureDisplayLoading ? 'Loading the architecture…' : archReviewStale ? 'The architecture changed. Reload before reviewing.' : undefined}>
        {spec && access.canGenerate && editingApprovedArchitecture ? <ArchitectureEditModeButton value={editMode} disabled={applying || running || architectureDisplayLoading} onClick={openEditMode}/> : null}
        {spec && access.isRoot && archStatus !== 'approved' ? <ApproveAsSelector actor={archActor} disabled={approvingArch || applying || running || dirty}/> : null}
        {spec && access.canGenerate ? <button className="arch-btn arch-btn-outline" disabled={!dirty || applying || running} onClick={apply}><Save size={15}/>{applying ? 'Saving…' : 'Save draft'}</button> : null}
        {spec && archReviewStale && !dirty ? <button className="arch-btn arch-btn-outline" disabled={running || applying || architectureDisplayLoading} onClick={() => { void refreshProjectMeta(true); reloadArchApprovals(); }}>Reload architecture</button> : null}
        {spec && architectureRights.approve && (!architectureRights.configured || !!archApprovals) && archStatus !== 'approved' && archActor.canApprove ? <button className="arch-btn arch-btn-primary" disabled={approvingArch || running || dirty || applying || archReviewStale} onClick={approveArchitecture}>{approvingArch ? 'Confirming…' : archReview?.action || 'Approve architecture'}</button> : null}
      </StageFooter>}>
        <EditorProjectMenu historyOnly hideTrigger canRestore={access.canGenerate} openRequest={architectureHistoryRequest} versions={versions} onRestore={restore} canEstimate={canEstimate} canPricing={stageAvailable.pricing} canProposal={stageAvailable.proposal} running={running} onEstimate={() => selectStage('estimate')} onPricing={() => selectStage('pricing')} onProposal={() => selectStage('proposal')}/>
      </StageHeader>
      {qaMsg ? <div className="arch-qa-banner">{qaMsg}</div> : null}
      {architectureSaveError ? <div className="arch-error-banner" role="alert"><span className="arch-error-banner-text">{architectureSaveError}</span>
        {editingApprovedArchitecture ? <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm" onClick={openEditMode}>Edit mode</button> : null}
        <button type="button" className="arch-error-banner-close" aria-label="Dismiss save error" onClick={() => setArchitectureSaveError(null)}><X size={16}/></button></div> : null}
      {runError ? (
        <div className="arch-error-banner" role="alert">
          <span className="arch-error-banner-text">✕ Generation failed: {runError}</span>
          <button
            className="arch-error-banner-close"
            type="button"
            aria-label="Dismiss error"
            onClick={() => setRunError(null)}
          >
            ✕
          </button>
        </div>
      ) : null}

      <div
        className={
          'arch-editor-body' +
          (sourcesCollapsed ? ' sources-collapsed' : '') +
          (structureCollapsed ? ' structure-collapsed' : '')
        }
        style={!access.canGenerate ? { gridTemplateColumns: 'minmax(0, 1fr)' } : undefined}
      >
        {access.canGenerate && <FilesPanel
          ref={filesPanelRef}
          open={leftOpen}
          onClose={() => setLeftOpen(false)}
          projectId={projectId}
          ready={project != null}
          files={files}
          initialTranscript={initialTranscript}
          initialExtra={initialExtra}
          initialPhases={initialPhases}
          onUpload={uploadFiles}
          onDeleteFile={deleteFile}
          onFileNoteSaved={onFileNoteSaved}
          onGenerate={generate}
          running={running}
          generating={running && runKind === 'generate'}
          collapsed={sourcesCollapsed}
          onSetCollapsed={setSourcesCollapsed}
          peeking={peek === 'left'}
          onPeekEnter={() => openPeek('left')}
          onPeekLeave={closePeek}
          onRailClick={focusSources}
        />}

        {/* canvas + run-progress overlay share a positioned wrapper so
            the overlay centres on the poster without touching SvgCanvas */}
        <div className="arch-canvas-col">
          <SvgCanvas
            ref={svgRef}
            documentId={projectId}
            workingRef={workingRef}
            selPath={selPath}
            onSelect={onCanvasSelect}
            canEditText={access.canGenerate && !applying && !running && !dirty}
            onApplyText={applyText}
          />
          {activeRun ? (
            <RunProgress run={activeRun} elapsedMs={elapsedMs} onStop={access.canGenerate ? stopRun : undefined} />
          ) : null}
        </div>

        {access.canGenerate && <aside
          className={
            'arch-right-panel' +
            (rightOpen ? ' open' : '') +
            (structureCollapsed ? ' collapsed' : '') +
            (structureCollapsed && peek === 'right' ? ' peek' : '')
          }
          onMouseEnter={structureCollapsed ? () => openPeek('right') : undefined}
          onMouseLeave={structureCollapsed ? closePeek : undefined}
          onFocus={structureCollapsed ? () => openPeek('right') : undefined}
          onBlur={structureCollapsed ? closePeek : undefined}
        >
          {/* folded rail — hovering peeks, clicking gives this side the room */}
          <button
            type="button"
            className="arch-structure-rail"
            title={
              dirty
                ? 'Show the editor — unsaved changes (folds the sources away)'
                : 'Show the editor (folds the sources away)'
            }
            aria-label={dirty ? 'Show the editor — unsaved changes' : 'Show the editor'}
            onClick={focusStructure}
          >
            <span className="arch-structure-rail-chev" aria-hidden="true">
              ⟨
            </span>
            <span className="arch-structure-rail-label">STRUCTURE</span>
            {dirty ? (
              <span className="arch-structure-rail-dot" aria-hidden="true" />
            ) : null}
          </button>

          <div className="arch-structure-content">
          {selPath ? (
            <>
              <div className="arch-panel-head arch-crumb-head">
                <nav className="arch-crumb" aria-label="Breadcrumb">
                  {crumbs.map((seg, i) => {
                    const last = i === crumbs.length - 1;
                    return (
                      <React.Fragment key={seg.path}>
                        {i > 0 ? (
                          <span className="arch-crumb-sep" aria-hidden="true">
                            ›
                          </span>
                        ) : null}
                        {last ? (
                          <span className="arch-crumb-cur">
                            {seg.label}
                            {KIND_LABEL[seg.kind] !== seg.label ? (
                              <span className="arch-crumb-kind">
                                {KIND_LABEL[seg.kind]}
                              </span>
                            ) : null}
                          </span>
                        ) : (
                          <button
                            className="arch-crumb-link"
                            type="button"
                            onClick={() => navSelect(seg.path)}
                          >
                            {seg.label}
                          </button>
                        )}
                      </React.Fragment>
                    );
                  })}
                </nav>
                <span className="arch-panel-head-tools">
                  {structureToggle}
                  <button
                    className="arch-btn arch-btn-ghost arch-btn-sm"
                    title="Back to structure"
                    aria-label="Back to structure"
                    onClick={clearSelection}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                </span>
              </div>
              <div className="arch-crumb-tools">
                <button
                  className="arch-btn arch-btn-ghost arch-btn-sm"
                  type="button"
                  onClick={clearSelection}
                >
                  <ArrowLeft size={14} aria-hidden="true" /> Structure
                </button>
              </div>
              {kindDesc ? <div className="arch-kind-desc">{kindDesc}</div> : null}
              <div className="arch-editor-form" ref={formBodyRef}>
                <ContextEditor
                  key={selPath + ':' + formRev}
                  workingRef={workingRef}
                  selPath={selPath}
                  markDirty={markDirty}
                  bumpForm={bumpForm}
                  selectPath={selectPath}
                />
              </div>
              <div className="arch-editor-actions">
                <button
                  className={'arch-btn arch-btn-primary' + (dirty ? ' dirty' : '')}
                  disabled={applying}
                  onClick={apply}
                >
                  {applying ? 'Applying…' : 'Apply'}
                </button>
                <button className="arch-btn arch-btn-ghost" disabled={applying} onClick={discard}>
                  Discard
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="arch-panel-head">
                <span className="arch-panel-heading"><Layers3 size={17} aria-hidden="true" /> Structure</span>
                <span className="arch-panel-head-tools">
                  {structureToggle}
                  <button
                    type="button"
                    className="arch-btn arch-btn-ghost arch-btn-sm arch-mobile-only"
                    aria-label="Close structure panel"
                    onClick={() => setRightOpen(false)}
                  ><X size={16} aria-hidden="true" /></button>
                </span>
              </div>
              <div className="arch-editor-form">
                {spec ? (
                  <>
                    <StructureTree
                      workingRef={workingRef}
                      formRev={formRev}
                      selPath={revealPath}
                      onSelect={navSelect}
                      onHover={onTreeHover}
                      query={treeQuery}
                      setQuery={setTreeQuery}
                      commentAnchorForPath={(path) => svgRef.current?.getBlockAnchor(path) || null}
                    />
                  </>
                ) : (
                  <div className="arch-empty">
                    Upload notes and press "Generate".
                  </div>
                )}
              </div>
              {dirty ? (
                <div className="arch-editor-actions">
                  <button
                    className={'arch-btn arch-btn-primary' + (dirty ? ' dirty' : '')}
                    disabled={applying}
                    onClick={apply}
                  >
                    {applying ? 'Applying…' : 'Apply'}
                  </button>
                  <button className="arch-btn arch-btn-ghost" disabled={applying} onClick={discard}>
                    Discard
                  </button>
                </div>
              ) : null}
            </>
          )}
          </div>
        </aside>}
      </div>

      <div
        className={'arch-drawer-backdrop' + (anyDrawer ? '' : ' arch-hidden')}
        onClick={() => {
          setLeftOpen(false);
          setRightOpen(false);
        }}
      />

      {access.canGenerate && <PromptBar
        active={activeStage === 'architecture'}
        model={model}
        setModel={setModel}
        running={running}
        thinking={running && runKind === 'prompt'}
        elapsedMs={elapsedMs}
        telemetry={telemetry}
        onSend={promptSend}
      />}

      </WorkspaceScene>}

      {canEstimate && visitedStages.estimate && <WorkspaceScene stage="estimate" active={activeStage === 'estimate' && stageAvailable.estimate}>
        {historicalEstimate ? <div className="arch-saved-stage"><LegacyDocuments projectId={projectId} showEstimates showProposals={false} presentation="workspace" onClose={() => selectStage('architecture')} /></div> : <>
        {(estimateGate?.locked || historicalEstimate) ? <div className="arch-estimate-stage-status">
          <EstimateApprovalGate view={estimateGate?.locked ? estimateGate : null} variant="web" />
          {historicalEstimate ? <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm arch-estimate-history-link" onClick={() => setLegacyHistoryOpen(true)}>Earlier estimate</button> : null}
        </div> : null}
        <EstimatePanel open revision={(versions[0]?.seq || 0) + documentRevision} presentation="workspace" historicalOnly={staleEstimate} estimateRunning={!!pipeline?.running?.estimate || pipelineResume.busy} active={activeStage === 'estimate' && stageAvailable.estimate}
          projectId={projectId} projectName={project?.name || ''}
          architectureApproved={pipelineView.architecture === 'approved'}
          onClose={() => selectStage('architecture')} onChanged={onEstimateChanged}
          onDraftStateChange={estimateDraftChanged} />
        </>}
      </WorkspaceScene>}

      {canPricing && visitedStages.pricing && <WorkspaceScene stage="pricing" active={activeStage === 'pricing' && stageAvailable.pricing}>
        {historicalPricing ? <div className="arch-saved-stage"><SavedPricingHistory projectId={projectId} /></div> :
            <PricingPanel open refreshKey={pricingRevision} presentation="workspace" active={activeStage === 'pricing' && stageAvailable.pricing}
              referenceOpen={pricingReferenceOpen} onToggleReference={(canEstimate || !estimateRights.configured) ? () => setPricingReferenceOpen((value) => !value) : undefined}
              reference={(canEstimate || !estimateRights.configured) ? <EstimateReferencePane pricingSource canOpenEstimate={canEstimate} projectId={projectId} active={activeStage === 'pricing' && stageAvailable.pricing} revision={documentRevision} onOpenEstimate={() => selectStage('estimate')}
                open={pricingReferenceOpen} onToggle={() => setPricingReferenceOpen((value) => !value)} /> : undefined}
              projectId={projectId} projectName={project?.name || ''}
              currentUserId={user?.id} canManagePricing={access.isRoot}
              onClose={() => selectStage('architecture')} onChanged={onPricingChanged}
              onDraftStateChange={pricingDraftChanged}
              onProceed={canProposal ? () => { setNextStageAfterApproval('proposal'); void refreshProjectMeta(); } : undefined} />}
      </WorkspaceScene>}

      {canProposal && visitedStages.proposal && <WorkspaceScene stage="proposal" active={activeStage === 'proposal' && stageAvailable.proposal}>
        {historicalProposal ? <div className="arch-saved-stage"><LegacyDocuments projectId={projectId} showEstimates={false} showProposals presentation="workspace" onClose={() => selectStage('architecture')} onReturnToWork={access.isRoot ? id => requestLeave(() => setRecoveryProposalId(id)) : undefined} /></div> : <ProposalPanel open revision={proposalRevision} presentation="workspace" active={activeStage === 'proposal' && stageAvailable.proposal}
          projectId={projectId} projectName={project?.name || ''} access={proposalRights} pipeline={pipelineView}
          onClose={() => selectStage('architecture')} onChanged={onProposalChanged}
          onDraftStateChange={proposalDraftChanged} />}
      </WorkspaceScene>}
      </div>

      {legacyHistoryOpen ? <LegacyDocuments projectId={projectId} showEstimates={hasEstimateHistory} showProposals={hasProposalHistory} onClose={() => setLegacyHistoryOpen(false)} onReturnToWork={access.isRoot ? id => requestLeave(() => { setLegacyHistoryOpen(false); setRecoveryProposalId(id); }) : undefined} /> : null}
      {access.isRoot && recoveryProposalId ? <WorkflowRecoveryDialog open projectId={projectId} proposalId={recoveryProposalId} onClose={() => setRecoveryProposalId(null)} onRestored={async () => {
        await refreshProjectMeta(true, true, true);
        setVisitedStages({ architecture: true, proposal: true });
        setDocumentRevision(value => value + 1); setPricingRevision(value => value + 1); setProposalRevision(value => value + 1);
        setActiveStage('proposal'); setLegacyHistoryOpen(false); setRecoveryProposalId(null); reloadArchApprovals();
      }} /> : null}
      <ArchitectureEditDialog open={!!editDialog} value={editChoice} onChange={setEditChoice} onCancel={closeEditDialog} onConfirm={acceptEditDialog}
        structuralOnly={editDialog?.purpose === 'structural'} title={editDialog?.title} confirmLabel={editDialog?.confirmLabel}/>

      <CommentsPanel open={commentsVisible} projectId={projectId} projectName={project?.name || ''}
        stages={commentStages} stage={activeStage} counts={commentCounts} onClose={() => setCommentsOpen(false)}
        anchor={commentWorkspace.anchor} onClearAnchor={commentWorkspace.clearAnchor}
        onDraftStateChange={commentDraftChanged}
        onPickAnchor={canWriteComments ? () => { setCommentsOpen(false); commentWorkspace.setPicking(true); } : undefined}
        focusedThreadId={commentWorkspace.focusedThreadId} onThreadsChange={acceptCommentThreads}
        onJumpToAnchor={async anchor => {
          const result = await commentWorkspace.jump(anchor);
          if (result !== 'found') toast(result === 'changed' ? 'This location has changed since the comment was added. The original excerpt is preserved in the thread.' : 'This comment refers to another document version or a removed block. The original excerpt is preserved in the thread.', 'info');
          else if (window.matchMedia('(max-width: 1199px)').matches) setCommentsOpen(false);
        }}
        onChanged={() => void loadCommentCounts()} />

    </div>
    </CommentAnchorProvider>
  );
}
